/**
 * CEC-570: exact decimal arithmetic for budget amounts issued in another currency.
 *
 * Finance freezes two base rates (units per reference currency) and sends them as canonical
 * decimal text. Every converted value is computed here with BigInt, never with binary floats,
 * so the page agrees with the stored rates and with the .NET `decimal` rounding used by
 * `/convert` (half away from zero).
 */

/** An exact decimal: `units / 10^scale`. */
export interface DecimalValue {
  units: bigint;
  scale: number;
}

/** Anything the page may hold as a decimal: a JS number, its text, or an exact value. */
export type DecimalInput = number | string | DecimalValue;

/** The two base rates of a conversion (units per reference currency). */
export interface IssueCurrencyRates {
  from: DecimalInput;
  to: DecimalInput;
}

export interface ConvertLineInput {
  quantity: DecimalInput;
  /** Net unit price in the budget currency, before discount. */
  budgetUnitPrice: DecimalInput;
  /** Discount percent (0 to 100). */
  discount: DecimalInput;
  /** Net amount of the line in the budget currency, after discount. */
  budgetNet: DecimalInput;
  /** Tax amount stored by the server for the line, or null when unknown. */
  storedTax: DecimalInput | null;
}

export interface ConvertedLine {
  unit: number;
  net: number;
  tax: number | null;
  gross: number | null;
}

export type IssueCurrencyParams =
  | { kind: 'none' }
  | { kind: 'convert'; issueCurrency: string; budgetCurrencyId: number; rateFrom: string; rateTo: string; echoRates: string }
  | { kind: 'error'; reason: string };

const AMOUNT_SCALE = 4;
const DECIMAL_TEXT = /^(-?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;
const ISO_CODE = /^[A-Z]{3}$/;
const CURRENCY_ID = /^[1-9][0-9]{0,9}$/;
const RATE_TEXT = /^(0|[1-9][0-9]{0,9})(\.[0-9]{0,7}[1-9])?$/;
const PARAM_NAMES = ['issueCurrency', 'budgetCurrencyId', 'rateFrom', 'rateTo'] as const;

const pow10 = (exponent: number): bigint => 10n ** BigInt(exponent);
const abs = (value: bigint): bigint => (value < 0n ? -value : value);

/** Parses `[-]d+[.d+][e+-d+]` (JS numbers go through `String(n)`, the shortest round-trip text). */
export function parseDecimal(input: DecimalInput): DecimalValue {
  if (typeof input === 'object') {
    return input;
  }
  if (typeof input === 'number' && !Number.isFinite(input)) {
    throw new Error(`Not a finite number: ${input}`);
  }

  const match = DECIMAL_TEXT.exec(String(input).trim());
  if (!match) {
    throw new Error(`Not a decimal: ${String(input)}`);
  }

  const [, sign, integer, fraction = '', exponent = '0'] = match;
  let units = BigInt(integer + fraction);
  let scale = fraction.length - Number(exponent);
  if (scale < 0) {
    units *= pow10(-scale);
    scale = 0;
  }
  return { units: sign ? -units : units, scale };
}

/** Plain decimal text of an exact value, keeping its scale. */
export function toDecimalText(value: DecimalValue): string {
  const digits = abs(value.units).toString().padStart(value.scale + 1, '0');
  const integer = digits.slice(0, digits.length - value.scale);
  const fraction = digits.slice(digits.length - value.scale);
  return `${value.units < 0n ? '-' : ''}${integer}${fraction ? `.${fraction}` : ''}`;
}

const toNumber = (value: DecimalValue): number => Number(toDecimalText(value));

function multiply(a: DecimalInput, b: DecimalInput): DecimalValue {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  return { units: left.units * right.units, scale: left.scale + right.scale };
}

/** Exact `a x b / c`, rounded to `scale` decimals half away from zero. */
export function mulDivRound(a: DecimalInput, b: DecimalInput, c: DecimalInput, scale: number): DecimalValue {
  const left = parseDecimal(a);
  const right = parseDecimal(b);
  const divisor = parseDecimal(c);
  if (divisor.units === 0n) {
    throw new Error('Division by zero');
  }

  const numerator = left.units * right.units * pow10(divisor.scale + scale);
  const denominator = divisor.units * pow10(left.scale + right.scale);
  const magnitude = abs(denominator);
  let quotient = abs(numerator) / magnitude;
  if (2n * (abs(numerator) % magnitude) >= magnitude) {
    quotient += 1n;
  }

  const negative = (numerator < 0n) !== (denominator < 0n);
  return { units: negative && quotient !== 0n ? -quotient : quotient, scale };
}

/** Rounds to the printed precision, half away from zero, through exact decimal text. */
export function roundDecimal(value: DecimalInput, scale: number): number {
  return toNumber(mulDivRound(value, 1, 1, scale));
}

/** Exact sum of decimals (4-decimal texts or JS numbers), without binary float drift. */
export function sumDecimal(values: readonly DecimalInput[]): number {
  const parsed = values.map(parseDecimal);
  const scale = parsed.reduce((max, v) => Math.max(max, v.scale), 0);
  const units = parsed.reduce((sum, v) => sum + v.units * pow10(scale - v.scale), 0n);
  return toNumber({ units, scale });
}

const r4 = (a: DecimalInput, b: DecimalInput, c: DecimalInput): DecimalValue => mulDivRound(a, b, c, AMOUNT_SCALE);

/** `R4(unit x rateTo / rateFrom)`: the conversion applied once per line (core `ConvertAndRound`). */
export function convertUnitPrice(budgetUnitPrice: DecimalInput, rates: IssueCurrencyRates): number {
  return toNumber(r4(budgetUnitPrice, rates.to, rates.from));
}

/**
 * Converts a line from the converted unit price, so that qty x unit = net:
 * net = R4(qty x unit x (100 - discount) / 100); tax = R4(storedTax x net / budgetNet), or the
 * stored tax converted with the rates when the budget net is zero; gross = net + tax (exact).
 */
export function convertLineAmounts(line: ConvertLineInput, rates: IssueCurrencyRates): ConvertedLine {
  const unit = r4(line.budgetUnitPrice, rates.to, rates.from);
  const discount = parseDecimal(line.discount);
  const remaining: DecimalValue = { units: 100n * pow10(discount.scale) - discount.units, scale: discount.scale };
  const net = r4(multiply(line.quantity, unit), remaining, 100);

  let tax: DecimalValue | null = null;
  if (line.storedTax !== null) {
    tax = parseDecimal(line.budgetNet).units !== 0n
      ? r4(line.storedTax, net, line.budgetNet)
      : r4(line.storedTax, rates.to, rates.from);
  }

  return {
    unit: toNumber(unit),
    net: toNumber(net),
    tax: tax && toNumber(tax),
    gross: tax && sumDecimal([net, tax]),
  };
}

/** `R4(amount x rateTo / rateFrom)`: a fixed tax amount converted with the same rule. */
export function convertFixedTax(amount: DecimalInput, rates: IssueCurrencyRates): number {
  return toNumber(r4(amount, rates.to, rates.from));
}

/**
 * Reads the four render parameters sent by finance. Fails closed: none present means no
 * conversion; all four valid means convert; anything else, or a `budgetCurrencyId` that differs
 * from the loaded budget currency, is an error.
 */
export function parseIssueCurrencyParams(
  params: { get(name: string): string | null },
  loadedBudgetCurrencyId: number | null | undefined,
): IssueCurrencyParams {
  const raw = Object.fromEntries(PARAM_NAMES.map((name) => [name, params.get(name)])) as Record<
    (typeof PARAM_NAMES)[number],
    string | null
  >;
  const present = PARAM_NAMES.filter((name) => raw[name] !== null);
  if (present.length === 0) {
    return { kind: 'none' };
  }
  if (present.length !== PARAM_NAMES.length) {
    return { kind: 'error', reason: 'incomplete issue currency parameters' };
  }

  const { issueCurrency, budgetCurrencyId, rateFrom, rateTo } = raw as Record<(typeof PARAM_NAMES)[number], string>;
  if (!ISO_CODE.test(issueCurrency)) {
    return { kind: 'error', reason: 'invalid issueCurrency' };
  }
  if (!CURRENCY_ID.test(budgetCurrencyId)) {
    return { kind: 'error', reason: 'invalid budgetCurrencyId' };
  }
  for (const rate of [rateFrom, rateTo]) {
    if (!RATE_TEXT.test(rate) || !/[1-9]/.test(rate)) {
      return { kind: 'error', reason: 'invalid rate' };
    }
  }
  if (Number(budgetCurrencyId) !== loadedBudgetCurrencyId) {
    return { kind: 'error', reason: 'budgetCurrencyId does not match the budget currency' };
  }

  return {
    kind: 'convert',
    issueCurrency,
    budgetCurrencyId: Number(budgetCurrencyId),
    rateFrom,
    rateTo,
    echoRates: `${rateFrom}/${rateTo}`,
  };
}
